import { useQueries, QueryClient } from 'react-query';
import axios, { AxiosError, RawAxiosRequestHeaders } from 'axios';
import { useNavigate, useParams, NavLink, Navigate } from 'react-router-dom';
import { IBookReview } from './IBookReview';
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { debounce } from 'lodash';
import { useAuth0 } from '@auth0/auth0-react';
import { ClipLoader } from 'react-spinners';
import { useRef, useState } from 'react';
import SpinnerModal from 'Components/SpinnerModal';
import { IImage } from 'Admin/Images/IImage';

const returnHome = (
  <NavLink to="/BookReviews">
    Resenhas
  </NavLink>
)

const queryClient = new QueryClient()

const pageStyle: React.CSSProperties = {
  textAlign: 'left'
}

const buttonDiv: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between'
}

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '200px auto',
  maxWidth: '1000px',
  columnGap: '1em',
  rowGap: '.5em',
  fontSize: '1.5em'
}

const authorPhotoDivStyle: React.CSSProperties = {
  maxWidth: '50px'
}

const authorPhotoStyle: React.CSSProperties = {
  maxWidth: '100%'
}

const headerImageStyle: React.CSSProperties = {
  maxWidth: '100px',
  width: '100%'
}

function EditBookReview() {
  const navigate = useNavigate();
  let { id } = useParams()
  const { user, getAccessTokenSilently, isLoading: auth0Loading } = useAuth0();
  const initialValues: IBookReview = {
    title: '',
    author: user?.name || '',
    content: '',
    header: ''
  }
  const [values, setValues] = useState(initialValues);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef<TinyMCEEditor | null>(null)
  const [authorPhotos, setAuthorPhotos] = useState<{ display: string, value: string, imageType?: string }[]>([]);
  const [bookReviewImages, setBookReviewImages] = useState<{ display: string, value: string, imageType?: string }[]>([]);
  const [initialBookReview, setInitialBookReview] = useState(initialValues)
  const [spinnerDialogText, setSpinnerDialogText] = useState("");

  const loadBookReview = async () => {
    let header: RawAxiosRequestHeaders = {
      'Content-Type': 'application/json'
    }
    const accessToken = await getAccessTokenSilently();
    header.Authorization = `Bearer ${accessToken}`;
    const bookReviewURL = `${process.env.REACT_APP_BACKEND_URL}bookReviews/${id}`;
    const data = await (await axios.get<any>(bookReviewURL, {
      headers: header
    })).data
    const bookReview: IBookReview = {
      title: data.bookReview.title,
      content: data.bookReview.content,
      dateSubmitted: new Date(data.bookReview.dateSubmitted),
      author: data.bookReview.author,
      header: data.bookReview.header,
      authorPhoto: data.bookReview.authorPhoto,
      bookReviewImage: data.bookReview.bookReviewImage
    }
    setValues(bookReview)
    setInitialBookReview(bookReview)

    if (authorPhotos.length > 0 && bookReview.authorPhoto) {
      if (!authorPhotos.some(x => x.value === bookReview.authorPhoto)) {
        authorPhotos.push({ value: bookReview.authorPhoto, display: bookReview.authorPhoto })
        setAuthorPhotos(authorPhotos);
      }
    }

    if (bookReviewImages.length > 0 && bookReview.bookReviewImage) {
      if (!bookReviewImages.some(x => x.value === bookReview.bookReviewImage)) {
        bookReviewImages.push({ value: bookReview.bookReviewImage, display: bookReview.bookReviewImage })
      }
      setBookReviewImages(bookReviewImages)
    }


    return bookReview
  }

  const getImages = async () => {
    let header: RawAxiosRequestHeaders = {
      'Content-Type': 'application/json'
    }
    const accessToken = await getAccessTokenSilently();
    header.Authorization = `Bearer ${accessToken}`;
    const imageUrl = `${process.env.REACT_APP_BACKEND_URL}images`;
    const data = await (await axios.get<any>(imageUrl, {
      headers: header
    })).data
    const mappedImages: { display: string, value: string, imageType?: string }[] =
      data.images.filter((x: IImage) => !x.hidden).map((x: IImage) => {
        return {
          display: x.name,
          value: x.imageUrl,
          imageType: x.imageType
        }
      })

    const authorPics = mappedImages.filter(x => x.imageType === 'userPhoto')
    if (values.authorPhoto && !authorPics.some(x => x.value === values.authorPhoto)) {
      authorPics.push({ value: values.authorPhoto, display: values.authorPhoto })
    }
    setAuthorPhotos(authorPics);
    const bookReviewPics = mappedImages.filter(x => x.imageType === 'bookReview')
    if (values.bookReviewImage && !bookReviewPics.some(x => x.value === values.bookReviewImage)) {
      bookReviewPics.push({ value: values.bookReviewImage, display: values.bookReviewImage })
    }
    setBookReviewImages(bookReviewPics)
    return data;
  }

  const handleChange = (e: (React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement>)) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value
    })
  }

  const handleEditorChange = (newValue: string) => {
    setValues({
      ...values,
      content: newValue
    })
  }

  const isFormValid = () => {
    return (values.title.length > 0 && values.author.length > 0 && values.content.length > 0 && values.header?.length > 0 && values !== initialBookReview)
  }
  const handleBlur = (e: (React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLTextAreaElement>)) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value
    })
  }
  const debouncedHandler = debounce(handleChange, 500);

  const save = async () => {
    setSpinnerDialogText("Salvando as mudan&ccedil;as na resenha")
    const bookReviewURL = `${process.env.REACT_APP_BACKEND_URL}bookReviews/${id}`;
    setSaving(true)
    const accessToken = await getAccessTokenSilently();
    let header: RawAxiosRequestHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    }
    const putBody: IBookReview = values;
    axios.put(bookReviewURL, putBody, {
      headers: header
    }).then(res => {
      console.log(res)
      queryClient.removeQueries(['editbookreview', id], { exact: true })
      alert('save successful')
    }).catch((err: AxiosError | Error) => {
      if (axios.isAxiosError(err)) {
        console.error(err.response?.data);
      }
      alert('save failed')
    }).finally(() => {
      setSaving(false)
    })
  }

  const deleteBookReview = async () => {
    const bookReviewURL = `${process.env.REACT_APP_BACKEND_URL}bookReviews/${id}`;
    setSpinnerDialogText("Apagando a Resenha")
    setSaving(true)
    const accessToken = await getAccessTokenSilently();
    let header: RawAxiosRequestHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    }
    axios.delete(bookReviewURL, {
      headers: header
    }).then(res => {
      console.log(res)
      queryClient.removeQueries(['editbookreview', id], { exact: true })
      alert('A resenha foi apagada')
      navigate("/BookReviews")
    }).catch((err: AxiosError | Error) => {
      if (axios.isAxiosError(err)) {
        console.error(err.response?.data);
      }
      alert('erro apagando a resenha')
    }).finally(() => {
      setSaving(false)
    })
  }

  const [bookReviewQuery, imagesQuery] = useQueries([
    {
      queryKey: ['editbookreview', id],
      queryFn: loadBookReview,
      enabled: !auth0Loading,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false
    },
    {
      queryKey: ['images'],
      queryFn: getImages,
      enabled: !auth0Loading
    }
  ]
  )
  if (user && !user['peloestudo/roles'].includes('Owner')) {
    return (<Navigate to="/" />)
  }

  if (bookReviewQuery.status === 'loading' || imagesQuery.status === 'loading') {
    return <div><ClipLoader /></div>
  }

  if (bookReviewQuery.status === 'error' || imagesQuery.status === 'error') {
    const errors: any[] = [];
    if (bookReviewQuery.error) {
      if (axios.isAxiosError(bookReviewQuery.error)) {
        if (bookReviewQuery.error.response?.status === 404) {
          return (<div>
            Resenha not found.
            <br />
            {returnHome}
          </div>)
        }
      }
      errors.push(bookReviewQuery.error)
    }
    if (imagesQuery.status === 'error') {
      errors.push(imagesQuery.error)
    }
    console.error(errors)
    return <div>Error loading Resenha</div>
  }

  if (bookReviewQuery.data !== undefined && imagesQuery.data !== undefined) {
    return (
      <section style={pageStyle}>
        <h2>Edita a Resenha</h2>
        <div style={gridStyle}>
          {bookReviewImages.length > 0 &&
            (<><label>Imag&eacute;m da Resenha</label>
              <div>
                {values.bookReviewImage &&
                  <div>
                    <img src={values.bookReviewImage} alt={values.title} style={headerImageStyle} />
                  </div>
                }
                <select value={values.bookReviewImage} name="bookReviewImage" onChange={handleChange}>
                  {!values.bookReviewImage &&
                    <option></option>
                  }
                  {bookReviewImages.map(x => {
                    return <option value={x.value}>{x.display}</option>
                  })}
                </select>
              </div>
            </>)
          }


          <label>T&iacute;tulo da Resenha:
          </label>
          <input
            type="text"
            name="title"
            onChange={debouncedHandler}
            onBlur={handleBlur}
            defaultValue={values.title}
          />
          <label>Colunista:
          </label>
          <input
            type="text"
            name="author"
            onChange={debouncedHandler}
            defaultValue={values.author}
            onBlur={handleBlur}
          />
          {authorPhotos.length > 0 &&
            <><label>Foto do Colunista</label>
              <div>
                {values.authorPhoto &&
                  <div style={authorPhotoDivStyle}>
                    <img src={values.authorPhoto} alt={`${values.author}`} style={authorPhotoStyle} />
                  </div>
                }
                <select value={values.authorPhoto} name="authorPhoto" onChange={handleChange}>
                  {!values.authorPhoto &&
                    <option></option>
                  }
                  {authorPhotos.map(x => {
                    return <option value={x.value}>{x.display}</option>
                  })}
                </select>
              </div></>
          }
          <label>Cabe&#231;alho:</label>
          <textarea name="header" onBlur={handleBlur} onChange={debouncedHandler} defaultValue={values.header} />
        </div>

        <Editor
          apiKey='c27sqmj1ce4fnrsz5n0bcciljvxysmhsu5mjm3jzxdr24amf'
          onInit={(evt, editor) => editorRef.current = editor}
          onEditorChange={(newValue, editor) => handleEditorChange(newValue)}
          value={values.content}
          init={{
            height: 500,
            plugins: 'preview code',
            toolbar: [
              { name: 'history', items: ['undo', 'redo'] },
              { name: 'styles', items: ['styles'] },
              { name: 'formatting', items: ['bold', 'italic', 'blockquote'] },
              { name: 'alignment', items: ['alignleft', 'aligncenter', 'alignright', 'alignjustify', 'alignnone'] },
              { name: 'indentation', items: ['outdent', 'indent'] },
              { name: 'preview', items: ['preview', 'code'] }
            ]
          }}
        />
        <div style={buttonDiv}>
          <button onClick={save} disabled={!isFormValid() || saving}>Submit</button>
          <button onClick={() => {
            if (window.confirm("Tem certeza que quer apagar? Não vai poder recuperar.")) { deleteBookReview() }
          }} disabled={saving}>Apagar</button>
        </div>
        {
          saving &&
          <SpinnerModal
            spinnerText={spinnerDialogText}
          />
        }
      </section >
    )
  }
  return <></>
}

export default EditBookReview;